import { createContext, useContext } from 'react';

import { mergeExplorations } from '@/core/exploration';

import {
  ExplorationContextProvider,
  useExplorationContext,
} from '../exploration/exploration-context';
import { useConversationRepositoryContext } from './conversation-repository-context';
import {
  ensureValidExploration,
  getVariableDefinitions,
  getQueryVariablesFromParameters,
} from '../utils';
import { useMetadataContext } from '../metadata-context';
import { Conversation } from '../types';

export interface ConversationExplorationContextValue {
  conversation: Conversation | undefined;
  isWithinConversation: boolean;
}

const defaultContextValue: ConversationExplorationContextValue = {
  conversation: undefined,
  isWithinConversation: false,
};

const ConversationExplorationContext =
  createContext<ConversationExplorationContextValue>(defaultContextValue);

export const useConversationExplorationContext = () => {
  const context = useContext(ConversationExplorationContext);
  return context;
};

interface ConversationExplorationProviderProps {
  conversationId: string | undefined;
  children: React.ReactNode;
}

export const ConversationExplorationContextProvider = (
  props: ConversationExplorationProviderProps,
) => {
  const { conversationId, children } = props;

  const { exploration: outerExploration, parameters } = useExplorationContext();
  const { getConversation, getConversationAsExploration, setConversationAsExploration } =
    useConversationRepositoryContext();
  const { models, metrics } = useMetadataContext();

  if (conversationId === undefined) {
    return children;
  }

  const conversation = getConversation(conversationId);

  if (conversation === undefined) {
    return children;
  }

  const conversationExploration = getConversationAsExploration(conversation.conversationId);

  if (conversationExploration === undefined) {
    return children;
  }

  const mergedExploration = mergeExplorations(outerExploration, conversationExploration);

  const getVariables = () =>
    getQueryVariablesFromParameters(getVariableDefinitions(mergedExploration), parameters).sort(
      (a, b) => a.key.localeCompare(b.key),
    );

  const innerExploration = ensureValidExploration(
    mergedExploration,
    models,
    metrics,
    getVariables(),
  );

  return (
    <ConversationExplorationContext.Provider
      value={{
        conversation,
        isWithinConversation: true,
      }}>
      <ExplorationContextProvider
        exploration={innerExploration}
        models={models}
        metrics={metrics}
        setExploration={(exploration) => {
          setConversationAsExploration(conversation.conversationId, {
            ...exploration,
            view: {
              ...exploration.view,
              cells: exploration.view.cells.filter(
                // Don't push outer exploration's cells as new cells in the conversation
                (cell) => !outerExploration.view.cells.some((c) => c.id === cell.id),
              ),
            },
          });
          return exploration;
        }}
        setParameters={() => {
          throw new Error('Cannot modify parameters in conversation');
        }}
        upsertExploration={() => {
          throw new Error('Cannot upsert exploration in conversation');
        }}
        deleteExploration={() => {
          throw new Error('Cannot delete exploration in conversation');
        }}
        resetExploration={() => {
          throw new Error('Cannot reset exploration in conversation');
        }}
        parameters={parameters}>
        {children}
      </ExplorationContextProvider>
    </ConversationExplorationContext.Provider>
  );
};
